import React from "react";
import PropTypes from "prop-types";
import {useTranslation} from "react-i18next";
import {RiSendPlaneFill, RiAttachmentLine, RiCloseLine} from "react-icons/ri";
import {Bar, Row, Input, SendButton, ErrorText, AttachBtn, Preview, PreviewName, PreviewRemove} from "./styles";

const ACCEPT = "image/*,video/mp4,video/3gpp,video/quicktime,audio/mpeg,audio/ogg,audio/mp4,audio/aac,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv";

/**
 * Barra de digitar da conversa do grupo. Enter (ou o botão) chama onSend(content) — texto —
 * ou onSendMedia(file, caption) quando há um anexo. A barra controla o estado local
 * (enviando/erro); NÃO faz append — a msg vem pelo eco WS.
 */
const GroupComposeBar = ({onSend, onSendMedia, canSend}) => {
    const {t} = useTranslation();
    const [text, setText] = React.useState("");
    const [file, setFile] = React.useState(null);
    const [sending, setSending] = React.useState(false);
    const [error, setError] = React.useState(false);
    const fileRef = React.useRef(null);

    const submit = async (e) => {
        if (e) e.preventDefault();
        if (sending || canSend === false) return;
        const content = text.trim();
        if (file) {
            setSending(true);
            setError(false);
            const res = await onSendMedia(file, content);
            setSending(false);
            if (res?.isAxiosError || res instanceof Error) {
                setError(true); // mantém o anexo pra reenviar
            } else {
                setFile(null);
                setText("");
            }
            return;
        }
        if (!content) return;
        setSending(true);
        setError(false);
        const res = await onSend(content);
        setSending(false);
        if (res?.isAxiosError || res instanceof Error) {
            setError(true); // mantém o texto pra reenviar
        } else {
            setText("");
        }
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter") submit(e);
    };

    const onPick = (e) => {
        const f = e.target.files && e.target.files[0];
        e.target.value = ""; // permite reescolher o mesmo arquivo
        if (f) setFile(f);
    };

    return (
        <Bar>
            {file && (
                <Preview>
                    <PreviewName>{file.name}</PreviewName>
                    <PreviewRemove type="button" onClick={() => setFile(null)} aria-label={t("words.remove")}><RiCloseLine/></PreviewRemove>
                </Preview>
            )}
            <Row onSubmit={submit}>
                <AttachBtn type="button" onClick={() => fileRef.current && fileRef.current.click()}
                           disabled={sending || canSend === false} aria-label={t("words.attach")}>
                    <RiAttachmentLine/>
                </AttachBtn>
                <input ref={fileRef} type="file" accept={ACCEPT} style={{display: "none"}} onChange={onPick}/>
                <Input
                    value={text}
                    disabled={sending || canSend === false}
                    placeholder={t("words.type_a_message")}
                    onChange={(e) => {
                        setText(e.target.value);
                        if (error) setError(false);
                    }}
                    onKeyDown={onKeyDown}
                />
                <SendButton type="submit" disabled={sending || (!text.trim() && !file) || canSend === false} aria-label={t("words.type_a_message")}>
                    <RiSendPlaneFill/>
                </SendButton>
            </Row>
            {error && <ErrorText>{t("words.something_wrong")}</ErrorText>}
        </Bar>
    );
};

GroupComposeBar.propTypes = {
    onSend: PropTypes.func.isRequired,
    onSendMedia: PropTypes.func,
    canSend: PropTypes.bool,
};

export default GroupComposeBar;
